/**
 * Requests content access from the server.
 * Returns a promise that resolves with the response JSON.
 *
 * @param {string}  contentToken The content token to request access for.
 * @param {boolean} checkOnly    Only check access, without logging a view or returning the content URL.
 * @return {Promise} Resolves with the parsed JSON response.
 */
function requestContentAccess(contentToken, checkOnly) {
	var language = document.documentElement.lang;
	var url = '/include/ajax/knowledge.php?action=play_content&content_token=' + encodeURIComponent(contentToken) + '&language=' + encodeURIComponent(language);
	if (checkOnly) {
		url += '&check_only=1';
	}

	return fetch(url)
		.then(function(response) {
			if (!response.ok) {
				console.error('Content access request failed:', response.status, response.statusText);
				return { success: false, error: 'Request failed with status ' + response.status };
			}
			return response.text().then(function(text) {
				try {
					return JSON.parse(text);
				} catch (e) {
					console.error('Content access response (not JSON):', text);
					return { success: false, error: 'Invalid JSON response.' };
				}
			});
		})
		.catch(function(error) {
			console.error('Content access request error:', error);
			return { success: false, error: error.message };
		});
}

/**
 * Returns a translated string from window.translations, or the fallback.
 *
 * @param {string} section  The translation section.
 * @param {string} key      The translation key.
 * @param {string} fallback The text to use when there is no translation.
 * @return {string} The translated text.
 */
function getText(section, key, fallback) {
	if (window.translations && window.translations[section] && window.translations[section][key]) {
		return window.translations[section][key];
	}
	return fallback;
}

/**
 * Builds the access page URL with the content token as a query parameter,
 * so the access and register pages can track what content was requested.
 *
 * @param {string} contentToken The content token that was denied.
 * @return {string} The access page URL.
 */
function getAccessUrl(contentToken) {
	var base_url;
	if (typeof window.contentAccessUrl === 'string' && window.contentAccessUrl.length > 0) {
		base_url = window.contentAccessUrl;
	} else {
		base_url = '/' + document.documentElement.lang + '/register';
	}

	if (contentToken) {
		base_url += (base_url.indexOf('?') === -1 ? '?' : '&') + 'content_token=' + encodeURIComponent(contentToken);
	}

	return base_url;
}

/**
 * Handles an access-denied response. Plays the preview video in the container
 * when the content has one, otherwise redirects to the access page.
 *
 * @param {string}           contentToken The content token that was denied.
 * @param {Object}           data         The access-denied response.
 * @param {HTMLElement|null} container    The element to play the preview video in.
 */
function handleAccessDenied(contentToken, data, container) {
	if (data && data.preview_url && container) {
		playPreview(container, data.preview_url, contentToken);
		return;
	}

	window.location.href = getAccessUrl(contentToken);
}

/**
 * Replaces the container content with the preview video, played with sound.
 * When the preview ends, an overlay offers access to the full content or a replay.
 *
 * @param {HTMLElement} container    The element to play the preview video in.
 * @param {string}      previewUrl   The preview video URL.
 * @param {string}      contentToken The content token, used for the access page link.
 */
function playPreview(container, previewUrl, contentToken) {
	var video = document.createElement('video');
	video.className = 'content-preview-video';
	video.controls = true;
	video.autoplay = true;
	video.playsInline = true;
	video.src = previewUrl;

	var overlay = document.createElement('div');
	overlay.className = 'content-preview-overlay d-none';

	var access_button = document.createElement('button');
	access_button.type = 'button';
	access_button.className = 'btn btn-light';
	access_button.textContent = getText('content', 'preview_get_access', 'Request access to watch the full video');
	access_button.addEventListener('click', function() {
		window.location.href = getAccessUrl(contentToken);
	});

	var replay_button = document.createElement('button');
	replay_button.type = 'button';
	replay_button.className = 'btn btn-link text-white';
	replay_button.textContent = getText('content', 'preview_replay', 'Replay preview');
	replay_button.addEventListener('click', function() {
		overlay.classList.add('d-none');
		video.currentTime = 0;
		video.play();
	});

	overlay.appendChild(access_button);
	overlay.appendChild(replay_button);

	video.addEventListener('ended', function() {
		overlay.classList.remove('d-none');
	});

	container.innerHTML = '';
	container.classList.add('content-preview');
	container.appendChild(video);
	container.appendChild(overlay);
	container.style.cursor = 'default';

	// Cards sit inside a link, so clicks on the player must not open the content page.
	container.onclick = function(event) {
		event.preventDefault();
		event.stopPropagation();
	};
}

/**
 * Handles a click on a card thumbnail that has a preview video but no inline player.
 * Opens the content page when the visitor has access, otherwise plays the preview.
 *
 * @param {Event}       event            The click event.
 * @param {HTMLElement} thumbnailElement The thumbnail container element.
 * @param {string}      contentToken     The content token to request.
 */
function playCardPreview(event, thumbnailElement, contentToken) {
	event.preventDefault();
	event.stopPropagation();

	var link = thumbnailElement.closest('a');

	requestContentAccess(contentToken, true).then(function(data) {
		if (data.success) {
			window.location.href = link.href;
		} else if (data.access_denied) {
			handleAccessDenied(contentToken, data, thumbnailElement);
		}
	});
}

/**
 * Loads content via the access endpoint and plays video inline.
 * Replaces the thumbnail with a video player on success.
 * Redirects to the access page if access is denied.
 *
 * @param {Event}       event            The click event.
 * @param {HTMLElement} thumbnailElement The thumbnail container element.
 * @param {string}      contentToken     The content token to request.
 */
function playVideo(event, thumbnailElement, contentToken) {
	event.preventDefault();
	event.stopPropagation();

	requestContentAccess(contentToken).then(function(data) {
		if (data.success) {
			// Create video element.
			var video = document.createElement('video');
			video.controls = true;
			video.autoplay = true;
			video.style.position = 'absolute';
			video.style.top = '0';
			video.style.left = '0';
			video.style.width = '100%';
			video.style.height = '100%';
			video.style.objectFit = 'cover';
			video.src = data.url;

			// Clear thumbnail and insert video.
			thumbnailElement.innerHTML = '';
			thumbnailElement.appendChild(video);
			thumbnailElement.style.cursor = 'default';
			thumbnailElement.onclick = null;
		} else if (data.access_denied) {
			handleAccessDenied(contentToken, data, thumbnailElement);
		}
	});
}

/**
 * Loads content via the access endpoint and starts playback on a page video player.
 * Sets the video source and plays on success.
 * Redirects to the access page if access is denied.
 *
 * @param {HTMLElement} playerElement The video element to play.
 * @param {string}      contentToken  The content token to request.
 */
function playContentVideo(playerElement, contentToken) {
	requestContentAccess(contentToken).then(function(data) {
		if (data.success) {
			playerElement.src = data.url;
			playerElement.play();

			// Hide play overlay if present.
			var overlay = playerElement.parentElement.querySelector('.content-play-overlay');
			if (overlay) {
				overlay.style.display = 'none';
			}
		} else if (data.access_denied) {
			handleAccessDenied(contentToken, data, playerElement.parentElement);
		}
	});
}

/**
 * Loads content via the access endpoint and opens the download URL.
 * Redirects to the access page if access is denied.
 *
 * @param {Event}  event        The click event.
 * @param {string} contentToken The content token to request.
 */
function downloadContent(event, contentToken) {
	event.preventDefault();
	event.stopPropagation();

	var container = event.currentTarget.closest('.media-viewer, .document-viewer');

	requestContentAccess(contentToken).then(function(data) {
		if (data.success) {
			window.open(data.url, '_blank');
		} else if (data.access_denied) {
			handleAccessDenied(contentToken, data, container);
		}
	});
}

/**
 * Navigates to a content viewing page.
 *
 * @param {string} contentUrl The URL of the content to view.
 */
function viewContent(contentUrl) {
	window.location.href = contentUrl;
}

/**
 * Loads content via the access endpoint and opens the PDF viewer modal.
 * Reads the document title from the triggering element's data-pdf-title attribute.
 * Redirects to the access page if access is denied.
 *
 * @param {Event}  event        The click event.
 * @param {string} contentToken The content token to request.
 */
function viewDocument(event, contentToken) {
	event.preventDefault();
	event.stopPropagation();

	var trigger = event.currentTarget;
	var container = trigger.closest('.media-viewer, .document-viewer');
	var title = (window.translations && window.translations.pdf && window.translations.pdf.document) ? window.translations.pdf.document : 'Document';
	if (trigger && trigger.getAttribute) {
		var attr_title = trigger.getAttribute('data-pdf-title');
		if (attr_title) {
			title = attr_title;
		}
	}

	requestContentAccess(contentToken).then(function(data) {
		if (data.success) {
			if (window.pdfViewer && typeof window.pdfViewer.openPDF === 'function') {
				window.pdfViewer.openPDF(data.url, title);
			} else {
				console.error('PDF viewer is not initialized.');
			}
		} else if (data.access_denied) {
			handleAccessDenied(contentToken, data, container);
		}
	});
}

/**
 * Loads content via the access endpoint and opens the external URL in a new tab.
 * Redirects to the access page if access is denied.
 *
 * @param {Event}  event        The click event.
 * @param {string} contentToken The content token to request.
 */
function openExternalContent(event, contentToken) {
	event.preventDefault();
	event.stopPropagation();

	var container = event.currentTarget.closest('.media-viewer, .document-viewer');

	requestContentAccess(contentToken).then(function(data) {
		if (data.success) {
			window.open(data.url, '_blank', 'noopener,noreferrer');
		} else if (data.access_denied) {
			handleAccessDenied(contentToken, data, container);
		}
	});
}

/**
 * Logs a view for a public external link. The link itself opens the URL in a new tab.
 *
 * @param {string} contentToken The content token to log a view for.
 */
function trackExternalLink(contentToken) {
	requestContentAccess(contentToken);
}

/**
 * Loads content via the access endpoint and sets the iframe src for embedded content.
 * Hides the play overlay on success. Redirects to the access page if access is denied.
 *
 * @param {Event}  event        The click event.
 * @param {string} contentToken The content token to request.
 */
function loadExternalEmbed(event, contentToken) {
	event.preventDefault();
	event.stopPropagation();

	// Capture overlay reference before the async call.
	var overlay = event.currentTarget;

	requestContentAccess(contentToken).then(function(data) {
		if (data.success) {
			var iframe = document.getElementById('external-embed');
			if (iframe) {
				iframe.src = data.url;
			}

			// Remove overlay so the iframe is interactive.
			if (overlay && overlay.parentNode) {
				overlay.parentNode.removeChild(overlay);
			}
		} else if (data.access_denied) {
			handleAccessDenied(contentToken, data, overlay ? overlay.parentNode : null);
		}
	});
}

// Public embeds load without a click, so register their view on page load.
document.addEventListener('DOMContentLoaded', function() {
	document.querySelectorAll('[data-track-view]').forEach(function(element) {
		requestContentAccess(element.dataset.trackView);
	});
});

// Attach to window so they are accessible from inline onclick handlers.
window.downloadContent = downloadContent;
window.loadExternalEmbed = loadExternalEmbed;
window.openExternalContent = openExternalContent;
window.playCardPreview = playCardPreview;
window.playContentVideo = playContentVideo;
window.playVideo = playVideo;
window.trackExternalLink = trackExternalLink;
window.viewContent = viewContent;
window.viewDocument = viewDocument;
